import React, { useEffect, useState } from "react";
import { SortDescriptor } from "@progress/kendo-data-query";
import {
  AdminCheckbox,
  AdminContentWrapper,
  AdminHeader,
  Loader,
  TableGrid,
  TableLastItem,
} from "../../components";
import { useAppDispatch, useAppSelector } from "../../store";
import { format } from "date-fns";
import appConfig from "../../util/appConfig";
import {
  GridCellProps,
  GridColumn,
  GridHeaderCellProps,
} from "@progress/kendo-react-grid";
import {
  clearAdminBehaelter,
  getAdminBehaelterverwaltung,
} from "../../store/slices/behaelterverwaltung";
import { de } from "date-fns/locale";

const statuses: { [key: string]: string } = {
  check: appConfig.logo === "calw" ? "Offen" : "Prüfen",
  accepted: "Fertig",
  declined: "Storniert",
  // inWork: appConfig.logo === "calw" ? "Offen" : "In Arbeit",
  error: "Fehler",
};

export const requestTypes: { [key: string]: string } = {
  defectBin: "Defekte Tonne",
  removeBin: "Tonne abgemeldet",
  disappearedBin: "Tonne verschwunden",
  updateBin: "Änderung der Tonnengröße",
  freeBioBin: "Verhältnis Bio / Rest",
  newBin: "Neue Tonne angemeldet",
};

const AdminBehaelterverwaltung = () => {
  const dispatch = useAppDispatch();
  const { isAdminLoaderActive } = useAppSelector((state) => state.admin);
  const { itemsCount, behaeltervervaltunData } = useAppSelector(
    (state) => state.behaelterverwaltung.admin
  );

  const [sort, setSort] = useState<Array<SortDescriptor>>([]);
  const [value, setValue] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [
    ,
    // datesChanged
    setDatesChanged,
  ] = useState({});
  const [status, setStatus] = useState<string[]>([]);
  const [requestType, setRequestType] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [allItemsSelected, setAllItemsSelected] = useState(false);

  const onDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    if (end || end === null) {
      setDatesChanged({ updated: true });
    }
    setEndDate(end);
  };

  const changeHandler = (val: string) => {
    setValue(val);
  };

  const pageChangeHandler = (page: number) => {
    setPage(page);
  };

  const pageSizeHandler = (size: number) => {
    setPage(1);
    setPageSize(size);
  };

  const changeStatuses = (st: string[]) => {
    setStatus([...st]);
  };
  const changeRequestType = (st: string[]) => {
    setRequestType([...st]);
  };

  const generateSelectOptions = (type: string) => {
    switch (type) {
      case "lra": {
        return [
          { label: statuses.accepted, value: "accepted" },
          { label: statuses.check, value: "check" },
          { label: statuses.declined, value: "declined" },
          { label: statuses.error, value: "error" },
        ];
      }
      case "calw": {
        return [
          { label: statuses.accepted, value: "accepted" },
          { label: "Offen", value: "check" },
          { label: statuses.declined, value: "declined" },
          { label: statuses.error, value: "error" },
        ];
      }
      default: {
        return [
          { label: statuses.accepted, value: "accepted" },
          { label: statuses.inWork, value: "inWork" },
        ];
      }
    }
  };

  const generateRequestTypes = (type: string) => {
    switch (type) {
      case "lra": {
        return [
          { label: requestTypes.defectBin, value: "defectBin" },
          { label: requestTypes.removeBin, value: "removeBin" },
          { label: requestTypes.disappearedBin, value: "disappearedBin" },
          { label: requestTypes.updateBin, value: "updateBin" },
          { label: requestTypes.freeBioBin, value: "freeBioBin" },
        ];
      }
      case "calw": {
        return [{ label: requestTypes.defectBin, value: "defectBin" }];
      }
      default: {
        return [];
      }
    }
  };

  useEffect(() => {
    const abortController = new window.AbortController();
    const signal = abortController.signal;

    dispatch(
      getAdminBehaelterverwaltung({
        direction: sort[0]?.dir,
        page,
        pageSize,
        searchValue: value,
        sort: sort[0]?.field,
        signal,
        startDate: startDate ? new Date(startDate).toUTCString() : startDate,
        endDate: endDate ? new Date(endDate).toUTCString() : endDate,
        status,
        requestType,
      })
    );

    return () => {
      if (signal && abortController.abort) {
        abortController.abort();
      }
    };
  }, [sort, page, pageSize, value, endDate, status, requestType]);

  useEffect(() => {
    return () => {
      dispatch(clearAdminBehaelter());
    };
  }, []);

  const allItemsSelectHandler = () => {
    if (!allItemsSelected) {
      setAllItemsSelected(true);
      const ids = behaeltervervaltunData.map((i) => i.id);
      setSelectedItems([...ids]);
      return;
    }
    if (allItemsSelected) {
      setAllItemsSelected(false);
      setSelectedItems([]);
      return;
    }
  };

  const selectItemHandler = (id: string) => {
    if (selectedItems.includes(id)) {
      const t = selectedItems.filter((i) => i !== id);
      return setSelectedItems([...t]);
    }

    if (!selectedItems.includes(id)) {
      return setSelectedItems((prev) => [...prev, id]);
    }
  };

  const selectorOptions = [
    {
      placeholder: "Bitte Status wählen",
      isActive: ["lra"].includes(appConfig.logo),
      defaultValue: undefined,
      selectOptions: generateSelectOptions(appConfig.logo).filter(
        (el) => !status.includes(el.value)
      ),
      value: status.map((el) => {
        return {
          value: el,
          label: statuses[el],
        };
      }),
      onChange: changeStatuses,
    },
    {
      placeholder: ["calw"].includes(appConfig.logo)
        ? "Bitte Auftragsart wählen"
        : "Bitte Abfallart wählen",
      isActive: ["lra", "calw"].includes(appConfig.logo),
      defaultValue: undefined,
      selectOptions: generateRequestTypes(appConfig.logo),
      value: requestType.map((el) => {
        return { value: el, label: requestTypes[el] };
      }),
      onChange: changeRequestType,
    },
  ];

  const FirstItem = (props: GridCellProps) => {
    return (
      <td
        // className="k-command-cell"
        style={
          {
            // backgroundColor: 'white',
          }
        }
      >
        <AdminCheckbox
          checked={selectedItems.includes(props.dataItem.id)}
          changeHandler={() => {
            selectItemHandler(props.dataItem.id);
          }}
        />
      </td>
    );
  };
  const FirstHeaderItem = (props: GridHeaderCellProps) => {
    return (
      <div
        style={{
          margin: "0",
          padding: "0",
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          // border: "none",
          // backgroundColor: "rgba(0, 0, 0, 0.02) !important",
        }}
        // className={style.qwe}
      >
        <AdminCheckbox
          checked={allItemsSelected}
          changeHandler={allItemsSelectHandler}
        />
      </div>
    );
  };

  return (
    <div>
      <AdminHeader pageTitle={"BEHÄLTERVERWALTUNG"} hasBtnGroup={false} />
      <AdminContentWrapper>
        <TableGrid
          data={behaeltervervaltunData.map((el) => {
            return {
              ...el,
              name: `${el.usersurname}, ${el.username} `,
              createdAt: el.createdAt
                ? format(new Date(el.createdAt), "dd.MM.yyyy HH:mm")
                : "",
              // date: el.date ? format(new Date(el.date), "dd.MM.yyyy") : "",
              status: el.status ? statuses[el.status] : "",
              requestType: el.requestType ? requestTypes[el.requestType] : "",
              isFreeBioBin: el?.isFreeBioBin ? "Ja" : "Nein",
            };
          })}
          sort={sort}
          sortable={true}
          onSortChange={setSort}
          items={
            [
              { field: "status", title: "Status" },
              { field: "createdAt", title: "Datum (Meldung)" },
              { field: "name", title: "Nachname, Vorname" },
              { field: "requestType", title: "Auftragsart" },
              ["calw"].includes(appConfig.logo)
                ? { field: "additionalInfo", title: "Zusätzliche Info" }
                : undefined,
            ].filter((el: any) => el !== undefined) as any
          }
          searchInputValue={value}
          onSearchInputValueChange={changeHandler}
          withPager={true}
          page={page}
          pageSize={pageSize}
          pageChangeHandler={pageChangeHandler}
          pageSizeHandler={pageSizeHandler}
          itemsCount={itemsCount}
          onDateChange={onDateChange}
          startDate={startDate}
          endDate={endDate}
          withDatePicker
          lastItem={<GridColumn cell={TableLastItem} width="50px" />}
          selectorOptions={selectorOptions}
          withExcelExport={["calw"].includes(appConfig.logo)}
          excelExportData={behaeltervervaltunData
            .filter((el) => selectedItems.includes(el.id))
            .map((el) => {
              return {
                Status: el?.status ? statuses[el?.status] : "",
                Erstellungsdatum: el?.createdAt
                  ? format(new Date(el?.createdAt), "dd.MM.yyyy, HH:mm", {
                      locale: de,
                    })
                  : "",
                Typ: el?.requestType
                  ? requestTypes[el?.requestType]
                  : el?.requestType,
                "Objekt ID": el?.objektId ? el?.objektId : "",
                Name: `${el?.usersurname}, ${el?.username}`,
                Telefonnummer: el?.phoneNumber ? el?.phoneNumber : "-",
                Inventarnummer: el?.inventarnr ?? "",
                Fraktion: el?.behfraktion ?? "",
                Volumen: el?.behvolumen ?? "",
                "KAV-Nummer": el?.kavNumbers?.length
                  ? el?.kavNumbers.toString().replaceAll(",", ", ")
                  : "",
                "Zusätzliche Info": el?.additionalInfo
                  ? el?.additionalInfo
                  : "",
              };
            })}
          excelExportItem={[
            {
              field: "Status",
              title: "Status",
            },
            {
              field: "Erstellungsdatum",
              title: "Erstellungsdatum",
            },
            {
              field: "Typ",
              title: "Typ",
            },
            {
              field: "Objekt ID",
              title: "Objekt ID",
            },
            {
              field: "Besitzerort",
              title: "Besitzerort",
            },
            {
              field: "Name",
              title: "Name",
            },
            {
              field: "Telefonnummer",
              title: "Telefonnummer",
            },
            {
              field: "Inventarnummer",
              title: "Inventarnummer",
            },
            {
              field: "Fraktion",
              title: "Fraktion",
            },
            {
              field: "Volumen",
              title: "Volumen",
            },
            {
              field: "KAV-Nummer",
              title: "KAV-Nummer",
            },
            {
              field: "Zusätzliche Info",
              title: "Zusätzliche Info",
            },
          ]}
          firstItem={
            ["calw"].includes(appConfig.logo) ? (
              <GridColumn
                cell={FirstItem}
                width="40px"
                headerCell={FirstHeaderItem}
              />
            ) : null
          }
        />
      </AdminContentWrapper>
      {isAdminLoaderActive && <Loader />}
    </div>
  );
};

export default AdminBehaelterverwaltung;
